import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSave, FaUsers, FaWallet, FaRupeeSign } from "react-icons/fa";
import Loader from "../components/Loader";
import SortableTh from "../components/SortableTh";
import JoiningFeeService from "../services/JoiningFeeService";
import { useProfile } from "../context/ProfileContext";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const JoiningFee = () => {
  const [searchParams] = useSearchParams();
  const parsedFilter = searchParams.get("filter");
  const initialFilter =
    parsedFilter === "pending" || parsedFilter === "all" ? parsedFilter : "paid";
  const { profile, assignModule } = useProfile();
  const isNewType = profile?.employeeType === "new";
  const taxModule =
    isNewType && assignModule?.taxesCommission?.enabled
      ? assignModule.taxesCommission
      : null;

  const [configLoading, setConfigLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [baseAmountInput, setBaseAmountInput] = useState("");
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState(initialFilter);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [sortConfig, setSortConfig] = useState({
    key: "joiningFeePaidAt",
    direction: "desc",
  });
  const sortConfigRef = useRef(sortConfig);

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const response = await JoiningFeeService.getConfig();
      if (response?.success) {
        setConfig(response.data);
        setBaseAmountInput(String(response.data.baseAmount ?? ""));
      }
    } catch (error) {
      console.error("Error fetching joining fee config:", error);
      toast.error("Failed to load joining fee configuration");
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const fetchPayments = useCallback(
    async (page = 1, overrides = {}) => {
      setPaymentsLoading(true);
      try {
        const effectiveSearch =
          overrides.search !== undefined ? overrides.search : search;
        const effectiveFilter =
          overrides.paymentFilter !== undefined
            ? overrides.paymentFilter
            : paymentFilter;

        const params = {
          page,
          limit: pagination.limit,
          sortBy: sortConfigRef.current.key,
          sortOrder: sortConfigRef.current.direction,
          filter: effectiveFilter,
        };
        if (effectiveSearch.trim()) params.search = effectiveSearch.trim();

        const response = await JoiningFeeService.getPayments(params);
        if (response?.success) {
          setPayments(response.data.payments || []);
          setPagination(response.data.pagination || pagination);
          setSummary(response.data.summary || null);
        }
      } catch (error) {
        console.error("Error fetching joining fee payments:", error);
        toast.error("Failed to load joining fee payments");
      } finally {
        setPaymentsLoading(false);
      }
    },
    [search, paymentFilter, pagination.limit]
  );

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    const parsed = searchParams.get("filter");
    const nextFilter =
      parsed === "pending" || parsed === "all" ? parsed : "paid";
    setPaymentFilter(nextFilter);
  }, [searchParams]);

  useEffect(() => {
    fetchPayments(1, { paymentFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentFilter]);

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    const baseAmount = Number(baseAmountInput);
    if (!baseAmount || baseAmount <= 0) {
      toast.warning("Enter a valid base amount greater than 0");
      return;
    }
    if (config && baseAmount === config.baseAmount) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const response = await JoiningFeeService.updateConfig(baseAmount);
      if (response?.success) {
        setConfig(response.data);
        setBaseAmountInput(String(response.data.baseAmount ?? ""));
        toast.success("Joining fee updated successfully");
        fetchPayments(pagination.page);
      } else {
        toast.error(response?.message || "Failed to update joining fee");
      }
    } catch (error) {
      console.error("Error updating joining fee:", error);
      toast.error(
        error.response?.data?.message || "Failed to update joining fee"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPayments(1);
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    sortConfigRef.current = next;
    fetchPayments(1);
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction:
          prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      sortConfigRef.current = next;
      fetchPayments(1);
      return next;
    });
  };

  const sortThClass =
    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hover:bg-gray-100";

  const sortTh = (columnKey, label) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={sortThClass}
      theme="teal"
    />
  );

  if (isNewType && !taxModule) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">
            You do not have access to the Joining Fee module.
          </p>
        </div>
      </div>
    );
  }

  const gstPreview = (() => {
    const base = Number(baseAmountInput);
    if (!base || base <= 0) return null;
    const gstPct = config?.gstPercentage ?? 18;
    const gstAmount = Math.round(base * (gstPct / 100) * 100) / 100;
    return {
      baseAmount: base,
      gstAmount,
      totalAmount: base + gstAmount,
      gstPercentage: gstPct,
    };
  })();

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Joining Fee</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage the one-time driver joining fee and view payment history. Drivers
          are charged the base amount plus {config?.gstPercentage ?? 18}% GST
          automatically.
        </p>
      </div>

      {/* Config */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Current Joining Fee
        </h2>
        {configLoading ? (
          <Loader />
        ) : (
          <form onSubmit={handleSaveConfig} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-lg bg-teal-50 border border-teal-100 p-4">
                <p className="text-xs font-medium text-teal-700 uppercase">
                  Base Amount
                </p>
                <p className="mt-1 text-xl font-bold text-teal-900">
                  {formatCurrency(config?.baseAmount)}
                </p>
              </div>
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-4">
                <p className="text-xs font-medium text-blue-700 uppercase">
                  GST ({config?.gstPercentage ?? 18}%)
                </p>
                <p className="mt-1 text-xl font-bold text-blue-900">
                  {formatCurrency(config?.gstAmount)}
                </p>
              </div>
              <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-4">
                <p className="text-xs font-medium text-indigo-700 uppercase">
                  Total (Driver Pays)
                </p>
                <p className="mt-1 text-xl font-bold text-indigo-900">
                  {formatCurrency(config?.totalAmount)}
                </p>
              </div>
              {/* <div className="rounded-lg bg-gray-50 border border-gray-100 p-4">
                <p className="text-xs font-medium text-gray-600 uppercase">
                  Setting Key
                </p>
                <p className="mt-1 text-sm font-mono text-gray-800 break-all">
                  {config?.settingLabel || "joining_fee_amount"}
                </p>
              </div> */}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-4 pt-2 border-t border-gray-100">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Update Base Amount ({config?.currency || "INR"})
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={baseAmountInput}
                  onChange={(e) => setBaseAmountInput(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. 500"
                />
              </div>
              {gstPreview &&
                Number(baseAmountInput) !== config?.baseAmount && (
                  <p className="text-sm text-gray-600 sm:pb-2">
                    Preview: {formatCurrency(gstPreview.baseAmount)} + GST{" "}
                    {formatCurrency(gstPreview.gstAmount)} ={" "}
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(gstPreview.totalAmount)}
                    </span>
                  </p>
                )}
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                <FaSave />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <div className="p-3 rounded-full bg-teal-100 text-teal-600">
              <FaUsers className="text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Drivers Paid</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.totalDriversPaid?.toLocaleString() ?? 0}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <FaWallet className="text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Payments Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.totalDriversPending?.toLocaleString() ?? 0}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
              <FaRupeeSign className="text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary.totalRevenue)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payments list */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Joining Fee Payments
          </h2>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="w-full lg:max-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Filter
                </label>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                >
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Driver name, phone, email, or code"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setPaymentFilter("paid");
                  fetchPayments(1, {
                    search: "",
                    paymentFilter: "paid",
                  });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        {paymentsLoading ? (
          <div className="p-8">
            <Loader />
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No joining fee payments found.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {sortTh("fullName", "Driver")}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Payment Status
                    </th>
                    {sortTh(
                      "joiningFeePaidAt",
                      paymentFilter === "pending"
                        ? "Created At"
                        : paymentFilter === "all"
                          ? "Date"
                          : "Paid At"
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Payment Mode
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((row) => (
                    <tr key={row.driverId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {row.profileImage ? (
                            <img
                              src={row.profileImage}
                              alt=""
                              className="h-9 w-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                              {row.driverName?.charAt(0) || "?"}
                            </div>
                          )}
                          <div>
                            <Link
                              to={`/drivers/detail/${row.driverId}`}
                              className="font-medium text-teal-700 hover:underline"
                            >
                              {row.driverName || "—"}
                            </Link>
                            <p className="text-xs text-gray-500">
                              {row.driverCode || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div>{row.phone || "—"}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[180px]">
                          {row.email || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            String(row.paymentStatus || "").toLowerCase() === "paid"
                              ? "bg-emerald-100 text-emerald-800"
                              : String(row.paymentStatus || "").toLowerCase() === "pending"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {row.paymentStatus || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {/*
                          pending: created date
                          paid: paid date
                          all: per-row by paymentStatus
                        */}
                        {formatDateTime(
                          paymentFilter === "pending" ||
                            (paymentFilter === "all" &&
                              String(row.paymentStatus || "").toLowerCase() === "pending")
                            ? row.driverCreatedAt || row.pendingSince || row.joiningFeePaidAt
                            : row.joiningFeePaidAt || row.payment?.paidAt
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatCurrency(row.payment?.amountPaid)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.payment?.paymentMode || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="max-w-[140px] truncate" title={row.payment?.referenceId || ""}>
                          {row.payment?.referenceId || "—"}
                        </div>
                        {row.payment?.orderId && (
                          <div className="text-xs text-gray-400 truncate max-w-[140px]" title={row.payment.orderId}>
                            {row.payment.orderId}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px]">
                        <span className="line-clamp-2" title={row.payment?.remarks || ""}>
                          {row.payment?.remarks || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages} (
                  {pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchPayments(pagination.page - 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => fetchPayments(pagination.page + 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JoiningFee;
